<script lang="ts">
  import type {
    CheckpointResults,
    CheckpointTest,
    LessonCheckpointTest,
  } from 'shared/definitions/checkpoint-definitions';
  import type { ClientUserStore } from 'shared/src';
  import { onMount } from 'svelte';
  import LoadingSpinner from '$lib/components/LoadingSpinner.svelte';
  import TextField from '$lib/components/TextField.svelte';
  import UTester from './UTester.svelte';
  import H from '$lib/components/H.svelte';
  import Button from '$lib/components/Button.svelte';
  import { errorProvider } from '$lib/error-handling/errorProvider';

  export let clientUserStore: ClientUserStore;
  export let test: CheckpointTest | LessonCheckpointTest;

  interface $$Events {
    testEnded: CheckpointResults;
  }

  let state:
    | 'loading'
    | 'awaitingPassword'
    | 'passwordError'
    | 'paymentRequired'
    | 'tooManyAttempts'
    | 'errored'
    | 'running' = 'loading';
  let testPassword: string;
  onMount(makeAttempt);

  let errorMessage = '';
  let attempt: Omit<CheckpointResults, 'createdAt' | 'updatedAt'>;
  function makeAttempt(pw?: string) {
    state = 'loading';
    clientUserStore.api
      .createCheckpointAttempt(test.id, test.options.name, pw)
      .then((newAttempt) => {
        attempt = newAttempt;
        state = 'running';
      })
      .catch((err: any) => {
        const status = err?.status || 500;
        errorMessage = err?.message || '';
        switch (status) {
          // 403 - password required
          case 403:
            state = pw ? 'passwordError' : 'awaitingPassword';
            return;
          // 402 - payment required
          case 402:
            state = 'paymentRequired';
            return;
          // 429 - too many attempts
          case 429:
            state = 'tooManyAttempts';
            return;
          // 500 - other
          default:
            state = 'errored';
            console.error(err);
            errorProvider.error(err);
        }
      });
  }

  let pwForm: HTMLFormElement;
</script>

{#if state === 'loading'}
  <LoadingSpinner />
{:else if state === 'awaitingPassword' || state === 'passwordError'}
  <form class="mx-auto max-w-screen-sm" bind:this={pwForm}>
    <p class="font-medium text-blue">A password is required to take this test:</p>
    <TextField
      class="mt-8"
      autofocus
      placeholder="password"
      label="Test password"
      bind:value={testPassword}
    />
    {#if state === 'passwordError'}
      <div class="text-red pb-4">Invalid password.</div>
    {/if}
    <div class="text-right">
      <Button
        on:click={() => {
          makeAttempt(testPassword);
          pwForm.checkValidity();
        }}>Start Test</Button
      >
    </div>
  </form>
{:else if state === 'paymentRequired'}
  <H level={3}>Subscription or License Required</H>
  <p class="my-4">
    Either an individual subscription, group license or testing license is required to take this
    test.
  </p>

  <p class="my-4">
    You can <a href="/app/dashboard?modal=subscribe-modal" class="a">subscribe to uTheory</a>, or if
    you expected to be part of a school's license, please contact your teacher to be sure they have
    enabled a group license for your class or have seats available in their testing license.
  </p>
{:else if state === 'tooManyAttempts'}
  <H level={2}>Too Many Attempts</H>
  <p>{errorMessage || 'Sorry, you have no remaining allowed attempts for this test.'}</p>
{:else if state === 'errored'}
  <H>Error Starting Test</H>
  <p>
    uTheory encountered an error when starting this test. We have sent details of the error to our
    technical staff. Please try refreshing the page, and if the error continues, contact us at
    support@utheory.com.
  </p>
  <p>{errorMessage}</p>
{:else if state === 'running'}
  <UTester on:testEnded {test} {clientUserStore} attemptId={attempt.id} />
{/if}
